package ui.home

import androidx.compose.animation.*
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import api.LanguageType
import common.frontend.api.Io
import common.frontend.api.isSuperAdmin
import compose.assets.Svg
import compose.assets.svg.Alert
import compose.assets.svg.Arrow_top_right
import compose.assets.svg.Chevron_down
import compose.assets.svg.Help_circle_outline
import compose.assets.svg.Chevron_right
import org.jetbrains.compose.resources.painterResource
import compose.assets.svg.Logout
import io.Repository
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import provider.LocalExternalsProvider
import provider.icon
import org.ssoft.common.tools.nowDateTime
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import tools.TabDefinition
import tools.findRoot
import tools.rememberString
import ui.ArgsInitial
import ui.device.detail.DeviceArgs
import ui.device.detail.editor.DeviceEditorArgs
import ui.widget.*
import kotlin.time.Clock

@Composable
@ComposableNavNode(ArgsInitial::class)
fun HomeUI() {
    with(HomeScope.rememberScope()) {
        when (val state = vm.navigationItems) {
            is DataState.Error,
            is DataState.Idle,
            is DataState.Loading -> Unit

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun HomeScope.Content(nnvl: List<TabDefinition>) {
    ExtraActions()

    if (drawerProvider.isEnabled) {
        ModalNavigationDrawer(
            drawerState = drawerProvider.state!!,
            drawerContent = {
                LeftPanel(nnvl)
            },
            content = {
                ContentPanel(Modifier.fillMaxSize(1F).fillMaxHeight())
            })
    } else {
        Row(Modifier.fillMaxSize()) {
            LeftPanel(nnvl)
            ContentPanel(Modifier.weight(1F).fillMaxHeight())
        }
    }
}

@Composable
fun HomeScope.ExtraActions() {
    val key = remember { args.key }

    LaunchedEffect(Unit) {
        val cat = args.category
        val sub = args.subcategory

        args.release()

        when (cat) {
            ArgsInitial.Category.UNKNOWN -> Unit
            ArgsInitial.Category.DEVICE -> when (sub) {
                ArgsInitial.Subcategory.UNKNOWN -> Unit
                ArgsInitial.Subcategory.REGISTRATION -> {
                    if (key.isNotEmpty()) {
                        if (login.permission.canDeviceInfo(Io.Permission.OWN_ADD)) {
                            nav.navTo_DeviceEditorUI(
                                args = DeviceEditorArgs(
                                    deviceId = -1,
                                    registrationDeviceKey = key
                                )
                            )
                        }
                    }
                }

                ArgsInitial.Subcategory.INFO -> {
                    key.toLongOrNull()?.also {
                        if (login.permission.canDeviceInfo(Io.Permission.OWN_VIEW)) {
                            nav.navTo_DevicePreviewUI(DeviceArgs(it))
                        }
                    }
                }
            }
        }
    }
}

private class AnnouncementEntry(
    val dt: LocalDateTime,
    val title: String,
    val message: String
) {
    companion object {
        private val all = listOf(
            AnnouncementEntry(
                dt = LocalDateTime(
                    date = LocalDate(2025, 5, 14),
                    time = LocalTime(hour = 15, minute = 0)
                ),
                title = "14.05. - 15.05. 2025\nScheduled server maintenance",
                message = """
                    14.05. 13:00 - 15:00
                    Mobile-controlled games may not work.
                """.trimIndent(),
            ),
            AnnouncementEntry(
                dt = LocalDateTime(
                    date = LocalDate(2025, 5, 15),
                    time = LocalTime(hour = 13, minute = 0)
                ),
                title = "15.05. 2025\nScheduled server maintenance",
                message = """
                    15.05. 11:00 - 13:00
                    The website may not work.
                """.trimIndent(),
            )
        )

        @Composable
        fun rememberAnn(): List<AnnouncementEntry> {
            return remember {
                all.sortedBy { it.dt }
                    .filter { it.dt > Clock.nowDateTime() }
            }
        }
    }
}

@Composable
fun Announcement() {
    var isExpanded by remember { mutableStateOf(false) }
    val rotation by animateFloatAsState(
        if (isExpanded) 90F else 0F
    )
    AnnouncementEntry.rememberAnn()
        .takeIf { it.isNotEmpty() }
        ?.also { values ->
            MaterialTheme(
                colorScheme = lightColorScheme()
            ) {
                Card(
                    onClick = { isExpanded = !isExpanded },
                    modifier = Modifier.fillMaxWidth()
                        .padding(8.dp),
                ) {
                    Column(
                        Modifier.fillMaxWidth()
                            .padding(8.dp)
                    ) {
                        values.firstOrNull()?.also {
                            Row(
                                Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Icon(
                                    imageVector = Svg.Alert,
                                    contentDescription = null,
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                )
                                Text(
                                    text = it.title,
                                    style = MaterialTheme.typography.titleMedium,
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                        .weight(1F)
                                )
                                Icon(
                                    imageVector = Svg.Chevron_right,
                                    contentDescription = null,
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                        .rotate(rotation)
                                )
                            }
                        }
                        AnimatedVisibility(
                            visible = isExpanded,
                            modifier = Modifier.fillMaxWidth(),
                            enter = fadeIn() + expandVertically(),
                            exit = fadeOut() + shrinkVertically()
                        ) {
                            Column(
                                Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Space(4.dp)
                                values.forEachIndexed { index, entry ->
                                    if (index != 0) HorizontalDivider()
                                    Text(entry.message)
                                }
                            }
                        }
                    }
                }
            }

        }
}

@Composable
fun HomeScope.ContentPanel(weight: Modifier) {
    Column(weight) {
        Announcement()
        NavigationChildHomeContent(Node.EmptyUI)
    }
    LaunchedEffect(vm.navigationItems.entry) {
        delay(50)
        vm.navigationItems.entry
            ?.takeIf { vm.navigationItem.isEmpty() }
            ?.firstOrNull()
            ?.also { target ->
                vm.setNavigationRoot(target.tag)
                nav.findChildNavigation(Graph.HomeContent)?.also {
                    it.navigateTo(target.node, target.args, true)
                }
            }
    }
}

@Composable
fun HomeScope.LeftPanel(nnvl: List<TabDefinition>) {
    val scrollState = rememberScrollState()
    ModalDrawerSheet(
        drawerShape = RoundedCornerShape(0.dp, 16.dp, 0.dp, 0.dp),
        modifier = Modifier
            .width(320.dp)
            .fillMaxHeight()
    ) {
        if (!display.isMobile || login.dashboards.isEmpty()) {
            NavDrawerHeader()
            HorizontalDivider(Modifier.padding(horizontal = 16.dp))
            Space(8.dp)
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1F)
                .verticalScroll(scrollState)
                .padding(16.dp),
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                LeftPanelListing(nnvl, 0.dp)
                if (display.isMobile && display.isLand()) {
                    HorizontalDivider(Modifier.padding(horizontal = 16.dp))
                    Space(16.dp)
                    FooterPanel()
                    Space(16.dp)
                }
            }
        }

        if (!display.isMobile || !display.isLand()) {
            HorizontalDivider(Modifier.padding(horizontal = 16.dp))
            Space(16.dp)
            FooterPanel()
            Space(16.dp)
        }

    }
}

@Composable
private fun HomeScope.LeftPanelListing(
    nnvl: List<TabDefinition>,
    offset: Dp
) {
    nnvl.forEachIndexed { index, itemDef ->
        LeftpanelItem(itemDef, index, offset, itemDef.submenu.isNotEmpty())
        if (itemDef.submenu.isNotEmpty()) {
            AnimatedVisibility(
                visible = itemDef.expanded.value,
                enter = expandVertically(),
                exit = shrinkVertically()
            ) {
                Column {
                    LeftPanelListing(itemDef.submenu, offset + 32.dp)
                }

            }
        }
    }
}

@Composable
private fun HomeScope.LeftpanelItem(itemDef: TabDefinition, index: Int, offset: Dp, containsSubmenu: Boolean) {
    val animatedTriangle by animateFloatAsState(
        if (itemDef.expanded.value) 90F
        else 0F
    )
    NavigationDrawerItem(
        selected = vm.navigationItem == itemDef.tag,
        modifier = Modifier.padding(start = offset),
        onClick = {
            with(itemDef) {
                onClick(index)
                if (itemDef.submenu.isEmpty()) drawerProvider.close()
            }
        },
        icon = {
            Icon(itemDef.icon, itemDef.label.rememberString())
        },
        label = {
            Text(itemDef.label.rememberString())
        },
        badge = if (!containsSubmenu) null
        else {
            {
                Icon(Svg.Chevron_right, null, modifier = Modifier.rotate(animatedTriangle))
            }
        }
    )
}

@Composable
private fun HomeScope.NavDrawerHeader() {
    AnimatedVisibility(
        Repository.serverInfo?.imageLogoHorizontal != null,
        enter = fadeIn() + expandVertically()
    ) {
        Repository.serverInfo?.imageLogoHorizontal?.also {
            if (it == -29L) {
                Image(
                    painter = painterResource(Res.drawable.logo_panel_dark),
                    contentDescription = null,
                    contentScale = ContentScale.FillWidth,
                    modifier = Modifier.fillMaxWidth()
                        .clickable { window.location.reload() }
                        .padding(16.dp)
                )
            } else {
                LazyImage(
                    id = it,
                    contentScale = ContentScale.Inside,
                    modifierPlaceHolder = Modifier.fillMaxWidth()
                        .clickable { window.location.reload() }
                        .padding(16.dp),
                    modifierContent = Modifier.fillMaxWidth())
            }
        }
    }
}


/**
 * Bottom of the left panel: account (logout menu), language picker and link to documentation.
 */
@Composable
private fun HomeScope.FooterPanel(modifier: Modifier = Modifier) {
    OutlinedCard(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp)
    ) {
        FooterAccount()
        HorizontalDivider()
        Row(Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
            FooterLang(Modifier.weight(1F))
            VerticalDivider()
            FooterHelp(Modifier.weight(1F))
        }
    }
}

@Composable
private fun HomeScope.FooterLang(modifier: Modifier = Modifier) {
    var isExpanded by remember { mutableStateOf(false) }
    val animation by animateFloatAsState(if (isExpanded) 180F else 0F)
    val selected = localization.language

    Box(modifier) {
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .clickable { isExpanded = !isExpanded }
                .padding(horizontal = 4.dp, vertical = 14.dp)
        ) {
            Image(
                imageVector = selected.icon,
                contentDescription = null,
                modifier = Modifier.size(20.dp).clip(CircleShape)
            )
            Space(8.dp)
            Text(
                text = selected.title,
                style = MaterialTheme.typography.bodyMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1F, fill = false)
            )
            Space(4.dp)
            Icon(
                imageVector = Svg.Chevron_down,
                contentDescription = null,
                modifier = Modifier.size(20.dp).rotate(animation)
            )
        }
        DropdownMenu(
            expanded = isExpanded,
            onDismissRequest = { isExpanded = false }
        ) {
            HomeScope.LanguageEntry.all
                .filter { it.entry != localization.language }
                .forEach { lang ->
                DropdownMenuItem(
                    modifier = Modifier.width(256.dp),
                    leadingIcon = {
                        Image(
                            imageVector = lang.entry.icon,
                            contentDescription = null,
                            modifier = Modifier.size(32.dp).clip(CircleShape)
                        )
                    },
                    text = { Text(lang.label) },
                    onClick = {
                        isExpanded = false
                        localization.change(lang.entry)
                    }
                )
            }
        }
    }
}

@Composable
private fun HomeScope.FooterHelp(modifier: Modifier = Modifier) {
    val externals = LocalExternalsProvider.current
    val url = docsUrl(Repository.serverInfo?.docsUrl, localization.language)
    val color = MaterialTheme.colorScheme.primary
    Row(
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier
            .fillMaxHeight()
            .clickable { externals.openInBrowser(url) }
            .padding(horizontal = 4.dp, vertical = 14.dp)
    ) {
        Icon(
            imageVector = Svg.Help_circle_outline,
            contentDescription = null,
            tint = color,
            modifier = Modifier.size(20.dp)
        )
        Space(8.dp)
        Text(
            text = Res.string.home_help.rememberString(),
            style = MaterialTheme.typography.bodyMedium,
            color = color,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1F, fill = false)
        )
        Space(4.dp)
        Icon(
            imageVector = Svg.Arrow_top_right,
            contentDescription = null,
            tint = color,
            modifier = Modifier.size(16.dp)
        )
    }
}

/**
 * White-label documentation in the portal language, e.g. `https://docs.screensoft.org/cs/`.
 * [base] is `SERVER.docs_url` (may be without scheme / with trailing slash).
 */
private fun docsUrl(base: String?, language: LanguageType): String {
    val host = base?.trim()?.trimEnd('/')?.takeIf { it.isNotEmpty() } ?: "docs.screensoft.org"
    val root = if (host.startsWith("http://") || host.startsWith("https://")) host else "https://$host"
    val dir = when (language) {
        LanguageType.English -> "en"
        LanguageType.Slovak -> "sk"
        LanguageType.Czech -> "cs"
        LanguageType.Hrvatska -> "hr"
        LanguageType.Bosnia -> "bs"
        LanguageType.Bulgarian -> "bg"
        // documentation does not exist in these languages yet -> English
        LanguageType.German,
        LanguageType.Spanish,
        LanguageType.Italian,
        LanguageType.French,
        LanguageType.Portuguese -> "en"
    }
    return "$root/$dir/"
}

private fun initials(name: String?): String = name
    ?.split(' ', '.', '-', '_')
    ?.filter { it.isNotBlank() }
    ?.take(2)
    ?.joinToString("") { it.first().uppercase() }
    ?: ""

@Composable
private fun HomeScope.FooterAccount() {
    val dialogState = DialogState.remember()
    LogoutDialog(dialogState)
    var isExpanded by remember { mutableStateOf(false) }
    val animation by animateFloatAsState(if (isExpanded) 0F else 180F)

    Box {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .clickable { isExpanded = !isExpanded }
                .padding(12.dp)
        ) {
            Box(
                contentAlignment = Alignment.Center,
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(MaterialTheme.colorScheme.secondaryContainer)
            ) {
                Text(
                    text = initials(account.current?.name),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSecondaryContainer
                )
            }
            Space(12.dp)
            Column(Modifier.weight(1F)) {
                Text(
                    text = account.current?.name ?: "",
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = account.current?.company?.name ?: "",
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Light,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (login.isSuperAdmin() && account.current?.company?.name != account.current?.server?.name) {
                    Text(
                        text = account.current?.server?.name ?: "",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Light,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            Space(8.dp)
            Icon(
                imageVector = Svg.Chevron_down,
                contentDescription = null,
                modifier = Modifier.rotate(animation)
            )
        }
        DropdownMenu(
            expanded = isExpanded,
            onDismissRequest = { isExpanded = false }
        ) {
            DropdownMenuItem(
                modifier = Modifier.width(256.dp),
                leadingIcon = { Icon(Svg.Logout, null) },
                text = { Text(Res.string.home_logout_title.rememberString()) },
                onClick = {
                    isExpanded = false
                    dialogState.show()
                }
            )
        }
    }
}

@Composable
private fun HomeScope.LogoutDialog(dialogState: DialogState) {
    Dialog(dialogState) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            title = {
                Text(Res.string.home_logout_title.rememberString())
            },
            onDismissRequest = { dialogState.hide() },
            confirmButton = {
                TextButton({
                    account.logout()
                    nav.findRoot().navTo_LoginUI(clearTop = true)
                }) {
                    Text(Res.string.dialog__action_yes.rememberString())
                }
            },
            dismissButton = {
                TextButton({
                    dialogState.hide()
                }) {
                    Text(Res.string.dialog__action_no.rememberString())
                }
            }) {
            Text(Res.string.home_logout_message.rememberString())
        }
    }
}
